"use client";

import { api } from "@/api";
import ClockIcon from "@/assets/icons/clock.svg";
import ReferenceIcon from "@/assets/icons/concept.svg";
import ImageIcon from "@/assets/icons/image-storyboard.svg";
import ModalCloseIcon from "@/assets/icons/modal-close.svg";
import MusicIcon from "@/assets/icons/music-icon.svg";
import VideoIcon from "@/assets/icons/video-storyboard.svg";
import { FullScreenModal } from "@/component/FullScreenModal";
import { ScrollView } from "@/component/ScrollView";
import { useArtifactsViewerData } from "@/hooks/useArtifactsViewerData";
import { threadStore } from "@/store/thread";
import { RawArtifact } from "@/types/artifact";
import { Spin, Tabs } from "antd";
import classNames from "classnames";
import { observer } from "mobx-react-lite";
import { useCallback, useEffect, useMemo, useState } from "react";
import useSWR from "swr";
import { ImageTab } from "./components/ImageTab";
import {
  hasLatestUsedItems,
  LatestUsedTab,
  saveLatestUsedItem,
} from "./components/LatestUsedTab";
import { MusicTab } from "./components/MusicTab";
import { ReferenceTab } from "./components/ReferenceTab";
import { ThreadSelector } from "./components/ThreadSelector";
import { VideoTab } from "./components/VideoTab";

import {
  assetsExplorerStore,
  AssetType,
  SelectedAsset,
  ViewMode,
} from "@/store/assetsExplorer";

export const AssetsExplorerModal = observer(function AssetsExplorerModal() {
  const {
    isOpen: open,
    threadId,
    fileType = "all",
    onSelect,
    stopPlayingSignal,
  } = assetsExplorerStore;

  const onClose = () => assetsExplorerStore.close();

  const [selectedThreadId, setSelectedThreadId] = useState(threadId);

  // Sync selectedThreadId when threadId changes from store
  useEffect(() => {
    setSelectedThreadId(threadId);
  }, [threadId]);

  // Fetch artifacts for the selected thread
  const { data: customArtifactsContent, isLoading: artifactsLoading } = useSWR(
    selectedThreadId && open ? ["get-artifacts", selectedThreadId] : null,
    async () => {
      const res = await api.threads.getThreadArtifacts(selectedThreadId!, {
        multi_version: true,
      });
      return res.data as RawArtifact;
    },
  );

  const isSwitchingThread = selectedThreadId !== threadId;
  const showContentLoading = artifactsLoading && isSwitchingThread;

  // Check if there are any latest used items matching the fileType
  const showLatestUsedTab = useMemo(() => {
    return hasLatestUsedItems(selectedThreadId, fileType);
  }, [selectedThreadId, fileType]);

  const [activeTab, setActiveTab] = useState<AssetType>("image");
  const [viewMode, setViewMode] = useState<ViewMode>("shots");

  // Update activeTab when showLatestUsedTab changes and current tab is "latest"
  // or when the current tab is not valid for the fileType
  useEffect(() => {
    if (!showLatestUsedTab && activeTab === "latest") {
      setActiveTab(fileType === "video" ? "video" : "image");
    }
    // If fileType is image and activeTab is video/music, switch to image
    if (
      fileType === "image" &&
      (activeTab === "video" || activeTab === "music")
    ) {
      setActiveTab("image");
    }
    // If fileType is video and activeTab is image/music, switch to video
    if (
      fileType === "video" &&
      (activeTab === "image" || activeTab === "music")
    ) {
      setActiveTab("video");
    }
  }, [showLatestUsedTab, activeTab, fileType]);

  const artifacts = useArtifactsViewerData(
    isSwitchingThread ? customArtifactsContent || null : undefined,
  );
  const rtcTasks = threadStore.rtcTasks;

  // Get data for each tab
  const imageData = useMemo(() => artifacts.imageStoryboard.data, [artifacts]);
  const videoData = useMemo(() => artifacts.videoStoryboard.data, [artifacts]);
  const musicData = useMemo(() => artifacts.music.data, [artifacts]);
  const referenceData = useMemo(
    () => artifacts.referenceImage.data,
    [artifacts],
  );

  // Collect all currently selected file paths for each shot
  const selectedFilePaths = useMemo(() => {
    const paths = new Set<string>();

    // Get selected image paths from each shot

    imageData.shots.forEach((shot) => {
      if (shot.start_frame_image_file_path) {
        paths.add(shot.start_frame_image_file_path);
      }
    });

    // Get selected video paths from each shot
    videoData.shots.forEach((shot) => {
      if (shot.video_clip_file_path) {
        paths.add(shot.video_clip_file_path);
      }
    });

    return paths;
  }, [imageData.shots, videoData.shots]);

  const handleSelect = useCallback(
    (asset: SelectedAsset) => {
      const { path, type } = asset;
      // Save to latest used based on the asset type
      if (type === "image" || type === "reference") {
        saveLatestUsedItem(threadId, "image", path);
      } else if (type === "video") {
        saveLatestUsedItem(threadId, "video", path);
      }
      // Note: music and latest tabs don't save to latest used

      onSelect?.(asset);
      onClose();
    },
    [threadId, onSelect, onClose],
  );

  const supportsViewMode = activeTab === "image" || activeTab === "video";

  // Helper to render tab label with icon
  const renderTabLabel = (icon: React.ReactNode, label: string) => (
    <span className="flex items-center gap-1.5">
      {icon}
      {label}
    </span>
  );

  // Build tab items array, conditionally including Latest Used tab
  const tabItems = useMemo(() => {
    const items = [];

    // Only include Latest Used tab if there are items
    if (showLatestUsedTab) {
      items.push({
        key: "latest",
        label: renderTabLabel(<ClockIcon className="w-4 h-4" />, "Latest Used"),
        children: (
          <ScrollView className="h-full">
            <LatestUsedTab
              threadId={selectedThreadId}
              fileType={fileType}
              onSelect={handleSelect}
              stopSignal={stopPlayingSignal}
            />
          </ScrollView>
        ),
      });
    }

    // Image tab - hidden when fileType is "video"
    if (fileType !== "video") {
      items.push({
        key: "image",
        label: renderTabLabel(<ImageIcon className="w-4 h-4" />, "Image"),
        children: (
          <ScrollView className="h-full">
            <ImageTab
              data={imageData}
              rtcTasks={rtcTasks}
              viewMode={viewMode}
              selectedFilePaths={selectedFilePaths}
              onSelect={handleSelect}
              stopSignal={stopPlayingSignal}
            />
          </ScrollView>
        ),
      });
    }

    // Video tab - hidden when fileType is "image"
    if (fileType !== "image") {
      items.push({
        key: "video",
        label: renderTabLabel(<VideoIcon className="w-4 h-4" />, "Video"),
        children: (
          <ScrollView className="h-full">
            <VideoTab
              data={videoData}
              rtcTasks={rtcTasks}
              viewMode={viewMode}
              selectedFilePaths={selectedFilePaths}
              onSelect={handleSelect}
              stopSignal={stopPlayingSignal}
            />
          </ScrollView>
        ),
      });
    }

    // Music tab - hidden when fileType is "image" or "video"
    if (fileType === "all") {
      items.push({
        key: "music",
        label: renderTabLabel(<MusicIcon className="w-4 h-4" />, "Music"),
        children: (
          <ScrollView className="h-full">
            <MusicTab
              data={musicData}
              onSelect={handleSelect}
              stopSignal={stopPlayingSignal}
            />
          </ScrollView>
        ),
      });
    }

    // Reference tab - always shown
    items.push({
      key: "reference",
      label: renderTabLabel(<ReferenceIcon className="w-4 h-4" />, "Reference"),
      children: (
        <ScrollView className="h-full">
          <ReferenceTab
            data={referenceData}
            rtcTasks={rtcTasks}
            onSelect={handleSelect}
          />
        </ScrollView>
      ),
    });

    return items;
  }, [
    showLatestUsedTab,
    selectedThreadId,
    fileType,
    handleSelect,
    imageData,
    rtcTasks,
    viewMode,
    selectedFilePaths,
    videoData,
    musicData,
    referenceData,
    stopPlayingSignal,
  ]);

  return (
    <FullScreenModal
      open={open}
      onCancel={onClose}
      classNames={{
        content: "bg-transparent!",
        mask: "bg-background-body!",
      }}
      noScroll={true}
      zIndex={600}
    >
      <div className="w-full h-full flex flex-col pt-5 pb-16 lg:pt-[40px] lg:pb-8">
        {/* Header */}
        <div className="flex items-center justify-between max-w-[1600px] w-full mx-auto px-5 lg:px-20">
          <h2 className="text-text-primary text-[20px] font-medium leading-normal m-0">
            Select from Assets
          </h2>

          <div className="flex items-center gap-4">
            <ThreadSelector
              activeThreadId={threadId}
              currentThreadId={selectedThreadId}
              onSelect={setSelectedThreadId}
            />
            <button
              type="button"
              className="relative lg:left-[50px] cursor-pointer bg-transparent border-0 p-0"
              onClick={onClose}
              aria-label="Close assets explorer modal"
            >
              <ModalCloseIcon className="w-5 h-5 text-gray-secondary" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 max-w-[1600px] w-full mx-auto px-5 lg:px-20 mt-5 lg:mt-8 flex flex-col overflow-hidden relative">
          {showContentLoading && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-background-body/50 backdrop-blur-sm">
              <Spin size="large" />
            </div>
          )}
          <Tabs
            key={selectedThreadId}
            activeKey={activeTab}
            onChange={(key) => setActiveTab(key as AssetType)}
            items={tabItems}
            className="h-full flex flex-col [&_.ant-tabs-content]:flex-1 [&_.ant-tabs-content]:h-full [&_.ant-tabs-content]:overflow-hidden [&_.ant-tabs-tabpane]:h-full"
            tabBarExtraContent={
              supportsViewMode && (
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    className={classNames(
                      "px-3 py-1 text-sm rounded-md transition-colors cursor-pointer border-0",
                      viewMode === "grid"
                        ? "bg-background-card text-text-primary"
                        : "bg-transparent text-text-tertiary hover:text-text-secondary",
                    )}
                    onClick={() => setViewMode("grid")}
                  >
                    Grid
                  </button>
                  <button
                    type="button"
                    className={classNames(
                      "px-3 py-1 text-sm rounded-md transition-colors cursor-pointer border-0",
                      viewMode === "shots"
                        ? "bg-background-card text-text-primary"
                        : "bg-transparent text-text-tertiary hover:text-text-secondary",
                    )}
                    onClick={() => setViewMode("shots")}
                  >
                    Shots
                  </button>
                </div>
              )
            }
          />
        </div>
      </div>
    </FullScreenModal>
  );
});
